import { yupResolver } from "@hookform/resolvers/yup"
import { useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { type AddSepaFormValues } from "../../../types"
import { PaymentEngagementType } from "../../../../enums"
import { useNewCompanyPaymentMethods } from "../../../../hooks/useNewCompanyPaymentMethods"
import { useSepaValidationSchema } from "./useSepaValidationSchema"
import { useContractorSepaPaymentMethod } from "./useContractorSepaPaymentMethod"
import { useFTESepaPaymentMethod } from "./useFTESepaPaymentMethod"
import { useCompanyAddress } from "../../../hooks/useCompanyAddress"

export const useNewAddSepaPaymentMethodForm = () => {
  const { paymentFlow } = useParams()
  const { accountAspect: companyId } = useAccountAspect()

  // Get company related data
  const { data: companyPaymentMethodsData } = useNewCompanyPaymentMethods({
    variables: { companyId },
  })
  const { loading: companyAddressLoading } = useCompanyAddress({
    variables: { companyId },
  })

  // FTE and Contractor specific hooks
  const { upsertFTEPaymentMethod, loading: fteLoading } = useFTESepaPaymentMethod()
  const {
    createContractorPaymentMethod,
    loading: contractorLoading,
    isReady,
  } = useContractorSepaPaymentMethod()

  const customerBankAccountId = companyPaymentMethodsData?.company.customerBankAccount?.id || ""
  const isFTEFlow = paymentFlow === PaymentEngagementType.Fte

  const validationSchema = useSepaValidationSchema(paymentFlow as PaymentEngagementType)
  const form = useForm({
    defaultValues: {
      name: "",
      iban: "",
      addressLine1: "",
      addressLine2: "",
      postalCode: "",
      city: "",
      region: "",
      countryCode: "",
      swiftBicCode: "",
      isUserSigner: true,
      email: "",
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (values: AddSepaFormValues) => {
    if (isFTEFlow) {
      return upsertFTEPaymentMethod(values, customerBankAccountId)
    } else {
      // For contractor the payment method is created at the end of the flow
      return createContractorPaymentMethod(values)
    }
  }

  const loading = fteLoading || contractorLoading

  return {
    ...form,
    loading,
    onSubmit,
    formIsReady: !companyAddressLoading && isReady,
  }
}
